import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Badge } from "../../components/ui/badge";
import { Loader2, Coins, TrendingUp, User, Cpu } from 'lucide-react';

interface ConsumptionRecord {
  id: string;
  userId: string | null;
  user?: { name: string | null; email: string };
  type: string;
  model: string | null;
  feature: string;
  amount: number;
  cost: number;
  createdAt: string;
}

export default function TokenConsumption() {
  const token = localStorage.getItem('token');

  const { data: logs, isLoading } = useQuery<ConsumptionRecord[]>({
    queryKey: ['admin-token-consumption-logs'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/admin/token-consumption`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  const formatAmount = (amount: number) => {
    return amount.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border shadow-xl overflow-hidden">
        <CardHeader className="border-b border-border bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Coins className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <CardTitle className="text-xl font-black text-foreground">Live API Consumption</CardTitle>
                <CardDescription className="text-muted-foreground font-medium">
                  Continuous log of all API and Token usage events.
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="bg-card text-primary border-primary/20 font-mono">
              Last {logs?.length || 0} events
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="rounded-none border-0 overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/80 sticky top-0 z-10">
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px] w-[180px]">Date</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">User</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Type</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Feature</TableHead>
                  <TableHead className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Model</TableHead>
                  <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Usage</TableHead>
                  <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(logs || []).map((log) => (
                  <TableRow key={log.id} className="border-border hover:bg-card/30 transition-colors group">
                    <TableCell className="text-muted-foreground font-mono text-[10px]">
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-primary/50" />
                        <span className="text-foreground font-medium text-xs truncate max-w-[120px]">
                          {log.user ? (log.user.name || log.user.email) : 'System'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-card text-muted-foreground border-border font-mono text-[9px] uppercase">
                        {log.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground text-xs font-bold">{log.feature}</span>
                    </TableCell>
                    <TableCell>
                      {log.model ? (
                        <span className="text-purple-400/70 text-[10px] font-mono">{log.model}</span>
                      ) : (
                        <span className="text-muted-foreground text-[10px] italic">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-foreground font-bold font-mono text-xs">
                        {formatAmount(log.amount)}
                      </span>
                      <span className="ml-1 text-[9px] text-muted-foreground font-bold uppercase">
                        {log.type === 'OPENAI' ? 'tk' : 'cr'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-green-400/90 font-black font-mono text-xs">
                        ${log.cost.toFixed(4)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {(!logs || logs.length === 0) && (
              <div className="py-20 text-center space-y-4">
                <div className="inline-flex p-4 bg-card/50 rounded-full">
                  <Coins className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-foreground font-bold">No consumption events yet</p>
                  <p className="text-muted-foreground text-sm">Waiting for API calls to be registered...</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
